/**
 * docVersionFact.ts — the deterministic version FACT for a living document,
 * derived from DATA (INCIDENT-20260901).
 *
 * The registry stamps `docVersion` onto every rendition of a document an edit
 * turn re-delivers. The cover-note line that tells the user "this is version N,
 * it replaces the earlier one" used to be composed into answer TEXT by the
 * task-completion persist seam — which made the durable copy differ from the
 * live copy of the same answer and defeated every duplicate-answer belt. It is
 * now rendered here, on the client, from the attachment rows, so every lane
 * (task, inline SSE, VM) shows the identical line and answer text is never
 * mutated for a presentational fact.
 *
 * D9: deterministic status copy in the house voice (lowercase, calm). Facts
 * only — any what-changed narrative stays model-authored.
 *
 * Pure; no React. Kept out of ChatAttachment.tsx so it is unit-testable
 * without the preview/download component graph.
 */
import type { SerializedAttachment } from './types';
import { hasLegacyDocVersionFactLine } from './answerDedupeNormalize';
import { detectSpanishLikely } from './stepLimitNarration';

/**
 * Returns the fact line, or null when nothing is versioned (v1 / unstamped),
 * when the message is the user's own, or when the LEGACY server-appended line
 * is already in the text (pre-fix rows must not show it twice).
 *
 * ONE document (its pdf + docx renditions share a docId) → the singular line
 * with its version. SEVERAL updated documents in one delivery → a plural line
 * with no number (each card's own chip carries its version; a single number
 * would misstate the others — review candidate, PR #1381).
 */
export function docVersionFactFor(
  attachmentsSerialized: ReadonlyArray<SerializedAttachment> | undefined,
  isUser: boolean | undefined,
  messageText: string | undefined,
): string | null {
  if (isUser) return null;
  const versioned = (attachmentsSerialized || []).filter(
    (a) => typeof a?.docVersion === 'number' && a.docVersion >= 2,
  );
  if (versioned.length === 0) return null;
  if (hasLegacyDocVersionFactLine(messageText)) return null;
  const es = detectSpanishLikely(messageText);
  // Distinct documents: by docId when stamped, else by name stem (renditions of
  // one document differ only by extension).
  const docKeys = new Set(
    versioned.map((a) =>
      typeof a.docId === 'string' && a.docId
        ? a.docId
        : String(a.name || '').replace(/\.[A-Za-z0-9]+$/, '').toLowerCase(),
    ),
  );
  if (docKeys.size > 1) {
    return es
      ? 'documentos actualizados — reemplazan las versiones anteriores en este hilo.'
      : 'updated documents — these replace the earlier versions in this thread.';
  }
  const version = Math.max(...versioned.map((a) => a.docVersion as number));
  return es
    ? `actualizado a la versión ${version} — reemplaza la versión anterior de este documento.`
    : `updated to version ${version} — this replaces the earlier version of this document.`;
}
